import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Box, Button, Card, CardContent, Menu, MenuItem, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { subDays } from 'date-fns'
import { useMemo, useState } from 'react'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { useAppointmentsInRange } from '../../api/analytics'
import { STATUS_COLORS } from '../../ReportsPage/constants.ts'
import {
  exportData,
  formatDateTime,
  transformSubType,
  transformType,
} from '../../utils/export.utils'
import { PieChartLoading } from '../Loading'

interface PieChartData {
  name: string
  value: number
  color: string
}

export const AppointmentStatusPieChart: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // Default to today - 7 days
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const today = new Date()
    return subDays(today, 7)
  })
  const [toDate, setToDate] = useState<Date | null>(() => new Date())

  // Format dates for API (RFC3339)
  const fromDateStr = fromDate ? fromDate.toISOString() : ''
  const toDateStr = toDate ? toDate.toISOString() : ''

  // Fetch data with status filter for dispatched and completed
  const { data: appointmentsData, isLoading } = useAppointmentsInRange(
    fromDateStr,
    toDateStr,
    'scheduleStartTime',
    'dispatched,completed',
    'live'
  )
  const appointments = appointmentsData?.data

  const chartData = useMemo<PieChartData[]>(() => {
    if (!appointments) {
      return []
    }

    const now = new Date()

    // Count appointments by status
    const statusCounts: Record<string, number> = {
      dispatched: 0,
      conducted: 0,
      completed: 0,
    }

    appointments.forEach(appointment => {
      const status = appointment.status.toLowerCase()

      if (status === 'completed') {
        statusCounts.completed++
      } else if (status === 'dispatched') {
        // Check if it's "conducted" (dispatched and scheduleStartTime + 3 hours < now)
        if (appointment.scheduleStartTime) {
          const scheduleStartTime = new Date(appointment.scheduleStartTime)
          const threeHoursAfterStart = new Date(scheduleStartTime.getTime() + 3 * 60 * 60 * 1000)

          if (threeHoursAfterStart < now) {
            statusCounts.conducted++
          } else {
            statusCounts.dispatched++
          }
        } else {
          // If no scheduleStartTime, treat as regular dispatched
          statusCounts.dispatched++
        }
      }
    })

    // Convert to chart data format
    return [
      {
        name: 'Dispatched',
        value: statusCounts.dispatched,
        color: STATUS_COLORS.dispatched,
      },
      {
        name: 'Conducted',
        value: statusCounts.conducted,
        color: STATUS_COLORS.conducted,
      },
      {
        name: 'Completed',
        value: statusCounts.completed,
        color: STATUS_COLORS.completed,
      },
    ].filter(item => item.value > 0) // Only include non-zero values
  }, [appointments])

  const totalAppointments = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0)
  }, [chartData])

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (format: 'csv' | 'xlsx') => {
    if (!appointments) return

    const now = new Date()
    const transformedData = appointments.map(appointment => {
      const status = appointment.status.toLowerCase()
      let derivedStatus = status
      if (status === 'dispatched' && appointment.scheduleStartTime) {
        const threeHoursAfterStart = new Date(
          new Date(appointment.scheduleStartTime).getTime() + 3 * 60 * 60 * 1000
        )
        if (threeHoursAfterStart < now) derivedStatus = 'conducted'
      }

      return {
        'Customer Name': appointment.customerName || '-',
        'Customer Address': appointment.customerAddress || '-',
        'Customer Phone': appointment.customerPhone || '-',
        'Customer Email': appointment.customerEmail || '-',
        UUID: appointment.uuid || '-',
        Status: derivedStatus,
        'Residential Units': appointment.residentialUnits || '-',
        'Creation Date': appointment.createdAt ? formatDateTime(appointment.createdAt) : '-',
        'Scheduled Date': appointment.scheduleStartTime
          ? formatDateTime(appointment.scheduleStartTime)
          : '-',
        'Status Changed': appointment.statusChangedAt
          ? formatDateTime(appointment.statusChangedAt)
          : '-',
        Type: transformType(appointment),
        SubType: transformSubType(appointment),
      }
    })

    exportData(transformedData, {
      filename: 'appointment_status_distribution',
      sheetName: 'Appointment Status',
      format,
    })

    handleExportClose()
  }

  if (isLoading) return <PieChartLoading title="Appointment Status Distribution" />

  return (
    <Card sx={{ height: '100%', minHeight: 350, boxShadow: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '1rem',
                marginTop: '7px',
              }}
            >
              Appointment Status Distribution
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportClick}
              disabled={!appointments || appointments.length === 0}
            >
              Export
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
              <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
              <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
            </Menu>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={newValue => setFromDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 180 },
                  },
                }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={newValue => setToDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 180 },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>

        {chartData.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 250,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No data available for the selected date range
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number, name: string) => [
                    `${value} (${((value / totalAppointments) * 100).toFixed(1)}%)`,
                    `${name}`,
                  ]}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                  formatter={(value: string) => (
                    <span style={{ color: '#2D3748', fontSize: '14px' }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default AppointmentStatusPieChart
